import { Button, ButtonGroup, Checkbox, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { FieldSet, IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";

import {
  DashboardApi,
  DashboardSearch,
  DashboardTaskGridDto,
  EDepartment,
  ERequestType,
  RequestApi,
  UnassignedRequestApi,
} from "../../../api";
import { CustomDataTable, ExportButton } from "../../../components";
import { useDialog, useGlobalData } from "../../../contexts";
import { useApiService, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { getRouteStr } from "../../../utils/RequestRouteUtil";
import { MyTaskSubCard } from "../todoList/MyTaskSubCard";

export interface IAccountingTaskListProps {
  isAssigned: boolean;
}

export const AccountingTaskList: React.FunctionComponent<IAccountingTaskListProps> = ({ isAssigned }) => {
  const api = useApiService(DashboardApi);
  const unassignedRequestApi = useApiService(UnassignedRequestApi);
  const requestApi = useApiService(RequestApi);
  const { currentLanguage } = useGlobalData();
  const { t } = useTl();
  const { showDialog } = useDialog();

  const selectionList = React.useMemo(() => [], []);
  const closableIdsList = React.useMemo(() => [], []);
  const [selectAll, setselectAll] = React.useState(false);

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5, 10, 15],
    pageSize: 10,
    sortKeys: { requestNumber: "ASC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: DashboardSearch) => {
      return api.baseSearch(
        DashboardSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
          department: EDepartment.Accounting,
          assigned: isAssigned,
        })
      );
    },
    [api, currentLanguage.languageId, isAssigned]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const openRequest = React.useCallback(
    async (requestId: number, requestType: ERequestType) => {
      const subRequestId = await requestApi.getSubRequestTypeId(requestId, requestType);
      const route = await getRouteStr(requestType, subRequestId, "view");
      window.open(`#${route}`, "_blank");
    },
    [requestApi]
  );

  const columnCheckBox = React.useCallback(
    (row: DashboardTaskGridDto) => {
      closableIdsList.push(row.requestId);
      return (
        <Checkbox
          checked={selectionList.find((c) => c === row.requestId)}
          onChange={() => {
            if (selectionList.includes(row.requestId)) {
              const index = selectionList.findIndex((e) => e === row.requestId);
              selectionList.splice(index, 1);
            } else {
              selectionList.push(row.requestId);
            }
          }}
        />
      );
    },
    [closableIdsList, selectionList]
  );

  const assignRequest = React.useCallback(
    async (requestList: number[]) => {
      if (requestList?.length === 0) {
        showDialog({
          message: t(ETLCodes.SelectionEmpty),
          title: t(ETLCodes.SelectionEmpty),
          buttons: [
            {
              resultType: "yes",
              text: t(ETLCodes.General_Ok),
              intent: Intent.PRIMARY,
            },
          ],
          onConfirmed: async () => {},
        });
      } else {
        showDialog({
          message: t(ETLCodes.AssignElementsQuestion),
          title: t(ETLCodes.Assign),
          onConfirmed: async () => {
            await unassignedRequestApi.assignRequests(true, requestList);
            search();
          },
        });
      }
    },
    [unassignedRequestApi, search, showDialog, t]
  );

  const toggleSelectAll = React.useCallback(() => {
    const value = !selectAll;
    if (value) {
      selectionList.splice(0, selectionList.length, ...closableIdsList);
    } else {
      selectionList.length = 0;
    }
    setselectAll(value);
  }, [closableIdsList, selectAll, selectionList]);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      isAssigned
        ? {
            computed: true,
            disableSorting: true,
            fieldName: "_toggle",
            autoFitContent: true,
            render: (item: DashboardTaskGridDto, _, { subPanelOpened, toggleSubPanel }) => (
              <Button
                icon={subPanelOpened ? "minus" : "plus"}
                onClick={() => toggleSubPanel()}
                disabled={item?.subTasks?.length === 0}
              />
            ),
          }
        : {
            header: () => <Checkbox onChange={toggleSelectAll} />,
            computed: true,
            disableSorting: true,
            fieldName: "selector",
            autoFitContent: true,
            render: columnCheckBox,
          },
      {
        header: () => t(ETLCodes.Request),
        fieldName: "requestNumber",
        render: (row: DashboardTaskGridDto) => (
          <Button
            intent={Intent.PRIMARY}
            minimal={true}
            onClick={() => openRequest(row.requestId, row.requestTypeCode)}
            text={row.requestNumber}
          />
        ),
      },
      {
        header: () => t(ETLCodes.RequestDate),
        fieldName: "requestDate",
      },
      {
        header: () => t(ETLCodes.RequestState),
        fieldName: "requestState",
      },
    ],
    [columnCheckBox, isAssigned, openRequest, t, toggleSelectAll]
  );

  return (
    <FieldSet
      title={t(ETLCodes.AccountingTasks)}
      rightElement={
        <ButtonGroup>
          <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={DashboardSearch} />
          {!isAssigned && (
            <Button
              text={t(ETLCodes.Assign)}
              onClick={() => assignRequest(selectionList)}
              intent={Intent.PRIMARY}
              minimal={true}
              icon={IconNames.TICK_CIRCLE}
            />
          )}
        </ButtonGroup>
      }
    >
      <CustomDataTable
        tableState={tableState}
        loading={loading}
        columns={columns}
        keyFieldName="requestId"
        renderSubComponent={(item: DashboardTaskGridDto) => <MyTaskSubCard data={item.subTasks} />}
      />
    </FieldSet>
  );
};
